import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useGroupAdmin } from '@/hooks/useGroupAdmin';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { getInitials } from '@/utils/stringUtils';
import {
  MatchPlayerSlot,
  useReplaceScheduledMatchPlayer,
} from '@/hooks/useReplaceScheduledMatchPlayer';
import { ScheduledMatch } from '@/types/scheduledMatch';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  match: ScheduledMatch;
  slot: MatchPlayerSlot | null;
}

export const ReplacePlayerDialog: React.FC<Props> = ({ open, onOpenChange, match, slot }) => {
  const replace = useReplaceScheduledMatchPlayer();
  const { isAdmin } = useGroupAdmin(match.group_id || undefined);
  const [mode, setMode] = useState<'members' | 'guests'>('members');
  const [guestQuery, setGuestQuery] = useState('');

  const currentPlayers = [
    match.team1_player1,
    match.team1_player2,
    match.team2_player1,
    match.team2_player2,
  ].filter(Boolean) as string[];

  const oldUserId = slot ? (match as any)[slot] as string | undefined : undefined;

  const { data: profiles, isLoading } = useQuery({
    queryKey: ['replace-player-candidates', match.group_id, match.id],
    queryFn: async () => {
      if (match.group_id) {
        const { data: members, error: memErr } = await supabase
          .from('group_members')
          .select('user_id')
          .eq('group_id', match.group_id);
        if (memErr) throw memErr;
        const ids = (members || []).map((m) => m.user_id);
        if (ids.length === 0) return [];
        const { data, error } = await supabase
          .from('profiles')
          .select('id, full_name, avatar_url')
          .in('id', ids);
        if (error) throw error;
        return data || [];
      }
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url');
      if (error) throw error;
      return data || [];
    },
    enabled: open,
  });

  const { data: guests, isLoading: guestsLoading } = useQuery({
    queryKey: ['replace-player-guests', match.group_id, guestQuery],
    queryFn: async () => {
      const q = guestQuery.trim().replace(/[%,()]/g, '');
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, avatar_url')
        .or(`full_name.ilike.%${q}%,username.ilike.%${q}%`)
        .limit(15);
      if (error) throw error;
      const memberIds = new Set((profiles || []).map((p) => p.id));
      return (data || []).filter((p) => !memberIds.has(p.id));
    },
    enabled: open && mode === 'guests' && guestQuery.trim().length >= 3,
  });

  const isGuestMode = mode === 'guests' && !!match.group_id;
  const available = (isGuestMode ? guests || [] : profiles || []).filter((p) => !currentPlayers.includes(p.id));

  const handleSelect = async (userId: string) => {
    if (!slot) return;
    await replace.mutateAsync({
      matchId: match.id,
      groupId: match.group_id,
      slot,
      newUserId: userId,
      oldUserId,
      matchDate: match.match_date,
      clubName: match.club?.club_name ?? null,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card max-w-md w-[95vw]">
        <DialogHeader>
          <DialogTitle>Replace Player</DialogTitle>
          <DialogDescription>
            Choose a player to take this spot in the scheduled match.
          </DialogDescription>
        </DialogHeader>
        {isAdmin && match.group_id && (
          <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
            <Button type="button" size="sm" variant={mode === 'members' ? 'default' : 'ghost'} className="min-h-[44px]" onClick={() => setMode('members')}>
              Suggested members
            </Button>
            <Button type="button" size="sm" variant={mode === 'guests' ? 'default' : 'ghost'} className="min-h-[44px]" onClick={() => setMode('guests')}>
              Add guest
            </Button>
          </div>
        )}
        <Command className="bg-card border rounded-md" shouldFilter={!isGuestMode}>
          <CommandInput
            placeholder={isGuestMode ? 'Search any player (min 3 letters)...' : 'Search players by name...'}
            {...(isGuestMode ? { value: guestQuery, onValueChange: setGuestQuery } : {})}
          />
          <CommandList className="max-h-[320px] overflow-y-auto">
            <CommandEmpty>{isGuestMode ? (guestQuery.trim().length < 3 ? 'Type at least 3 letters to find a guest outside this group.' : guestsLoading ? 'Searching...' : 'No players found outside this group.') : isLoading ? 'Loading...' : 'No players found.'}</CommandEmpty>
            <CommandGroup>
              {available.map((p) => (
                <CommandItem
                  key={p.id}
                  value={`${p.full_name} ${p.id}`}
                  onSelect={() => handleSelect(p.id)}
                  className="flex items-center p-2 cursor-pointer hover:bg-muted rounded-sm"
                >
                  <Avatar className="w-8 h-8 mr-3">
                    <AvatarImage src={p.avatar_url || undefined} />
                    <AvatarFallback>{getInitials(p.full_name || '?')}</AvatarFallback>
                  </Avatar>
                  <span className="font-medium truncate">{p.full_name}</span>
                  {isGuestMode && <Badge variant="outline" className="ml-2 text-[10px]">Guest</Badge>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
};
